import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { AnimatedNumber } from '../../lib/AnimatedNumber'
import { accessHeadline, meterModel, type OrgKycStatus } from '../../lib/orgAccess'

/**
 * SeatMeter — the one continuous object on the team surface.
 *
 * `orgs/{id}.seats` holds THREE numbers (`used`, `pendingInvites`, `limit`)
 * and every product renders them as one caption. This draws them as three
 * materials (see `.seat-seg--*` in index.css), because the question an owner
 * actually has is not "how many" but "who is holding what": a colleague, an
 * invite that grants nothing yet, or a seat that is paused rather than free.
 *
 * ── IT IS ALSO THE PAYWALL'S HERO ─────────────────────────────────────────
 * The same element carries `layoutId="seat-meter"`, so the purchase sheet
 * UNFOLDS OUT OF the meter rather than appearing on top of it. Today the
 * codebase has exactly two `layoutId`s and both are nav underlines; this is
 * the first one that carries meaning.
 *
 * `shared={false}` is the guard for the one case where a shared layout looks
 * broken rather than magic: if the source meter is scrolled out of view,
 * framer flies the element from an off-screen origin. The caller measures and
 * turns the shared id off; the sheet then simply crossfades, which is the
 * correct degradation and costs three lines instead of a `<Continuum>`
 * framework.
 *
 * Reduced motion: the pip breathe has its `animation: none` block in the same
 * commit as its `@keyframes`, and the count-up is skipped by
 * `AnimatedNumber`'s own `useReducedMotion`. `MotionConfig reducedMotion="user"`
 * (set at both shell roots) collapses the layout animation to a crossfade —
 * asserted in the test rather than re-implemented here.
 */
export function SeatMeter({
  used,
  pendingInvites,
  paused = 0,
  limit,
  orgKyc,
  orgName,
  activeCount,
  onAddSeat,
  shared = true,
  animateCount = false,
  compact = false,
}: {
  used: number
  pendingInvites: number
  /** Suspended members. They hold NO seat, but drawing them as free would
   *  tell an owner their colleague is gone rather than paused. */
  paused?: number
  limit: number
  orgKyc: OrgKycStatus
  orgName: string
  /** Members who can actually see prices right now. */
  activeCount: number
  /** When present the last free pip becomes a real button — the entry for an
   *  owner who is SHOPPING rather than acting, so they can reach the price
   *  without first triggering an invite that fails. */
  onAddSeat?: () => void
  /** Off when the source is off-screen; see the header. */
  shared?: boolean
  /** Count up only when the number changed because of something the user
   *  just did. Never on mount, never on a re-render. */
  animateCount?: boolean
  /** Sheet hero variant — no headline sentence (the sheet says its own). */
  compact?: boolean
}) {
  const reduce = useReducedMotion()
  const model = meterModel({ used, pendingInvites, paused, limit })

  /**
   * ── THE ANNOUNCEMENT IS A DELTA, NOT A LIVE TWEEN ─────────────────────────
   * The seat caption used to sit inside `aria-live="polite"` while
   * <AnimatedNumber/> tweened through it for 380ms — the exact combination
   * `AnimatedNumber`'s own docblock forbids, because a rAF tween sets state
   * ~25 times and a live region queues an announcement for every one of them.
   * A screen-reader user inviting their fourth teammate heard "three… three…
   * three… four" for a second and a half.
   *
   * So the caption is no longer live at all, and this region carries ONE
   * settled sentence, published only when the numbers actually changed. It
   * stays EMPTY until then, so it adds nothing to a read-through of the page
   * — the pip group's own label already states the same fact on arrival.
   */
  const [announcement, setAnnouncement] = useState('')
  const spoken = useRef<string | null>(null)
  useEffect(() => {
    if (spoken.current === null) { spoken.current = model.label; return }
    if (spoken.current === model.label) return
    spoken.current = model.label
    setAnnouncement(model.label)
  }, [model.label])

  return (
    <motion.div
      {...(shared ? { layoutId: 'seat-meter' } : {})}
      className="flex flex-col gap-2"
      data-testid="seat-meter"
    >
      {/* `role="img"` prunes its own subtree from the accessibility tree, so
          with "Add a seat" inside it the one purchase entry point on this
          surface was unreachable to a screen reader. A group keeps the same
          one-sentence label AND keeps the button. */}
      <div
        className="flex items-center gap-1.5"
        role={onAddSeat ? 'group' : 'img'}
        aria-label={model.label}
      >
        {model.segments.map((s) => {
          if (s.kind === 'next' && onAddSeat) {
            return (
              <motion.button
                key={s.key}
                type="button"
                layout={!reduce}
                onClick={onAddSeat}
                className="seat-seg seat-seg--next"
                aria-label="Add a seat"
                data-testid="seat-seg-next"
              />
            )
          }
          return (
            <motion.span
              key={s.key}
              layout={!reduce}
              className={`seat-seg seat-seg--${s.kind}${s.kind === 'reserved' ? ' seat-seg-hold' : ''}`}
              data-testid={`seat-seg-${s.kind}`}
            />
          )
        })}
        {model.overflow > 0 && (
          <span className="tabular shrink-0 pl-1 text-[12px] font-medium text-soft" aria-hidden="true">
            +{model.overflow}
          </span>
        )}
      </div>

      {/* NOT a live region — see the note above the announcer. */}
      <p className="text-sm text-soft">
        <AnimatedNumber
          value={used}
          animateOnMount={false}
          duration={animateCount ? 380 : 0}
          reserve="99"
          testId="seat-count"
        />
        {' of '}
        <AnimatedNumber
          value={limit}
          animateOnMount={false}
          duration={animateCount ? 380 : 0}
          reserve="99"
          testId="seat-limit"
        />
        {limit === 1 ? ' seat' : ' seats'}
        {pendingInvites > 0 && (
          <> · <span className="tabular">{pendingInvites}</span> held by {pendingInvites === 1 ? 'an invite' : 'invites'}</>
        )}
      </p>

      {/* One announcement, after the count has settled. */}
      <span className="sr-only" aria-live="polite" data-testid="seat-meter-live">{announcement}</span>

      {!compact && (
        <p className="text-sm text-ink" data-testid="seat-headline">
          {accessHeadline({ orgKyc, orgName, activeCount, pausedCount: paused })}
        </p>
      )}
    </motion.div>
  )
}
